import React, { useEffect, useState } from 'react';
import { Card, ListGroup, ListGroupItem, Modal, Spinner as ReactSpinner } from 'react-bootstrap';
import { AppState, dataActions, useUser } from 'reducers';
import store from 'store';
import { useSelector } from 'react-redux';
import CartTable from 'components/CartTable';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import Spinner from 'components/Spinner';
import ButtonComponent from 'components/ButtonComponent';
import { DetailedOrder, RooferInfo, ProductInOrder, UserDeliveryAddress, OrderStep } from 'lib/types';
import NewOrderForm from './NewOrderForm';
import { getRoofer } from 'lib/communication/userInfo';
import { addOrder, appendOrder, modifyOrder } from 'lib/communication/orders';
import { cartMethods } from 'business_logic';
import { NewOrderState } from './NewOrder';
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import useIsMounted from 'lib/hooks/useIsMounted';
import { useDevice } from 'lib/hooks/useDevice';

interface NewOrderSummaryProps {
    goToAssortmentSelection: () => void;
    goToSummary: () => void;
    goToOrderForm: () => void;
    detailedOrder: DetailedOrder;
    completeAction: () => void;
    setIsCancelModeOn: (isCancelModeOn: boolean) => void;
    isCancelModeOn: boolean;
}

const NewOrderSummary = (props: NewOrderSummaryProps) => {
    const history = useHistory<NewOrderState>();
    const step = history.location.state?.step ?? OrderStep.OrderSummary;
    const edit = useSelector((state: AppState) => state.data.orders.edit);
    const append = useSelector((state: AppState) => state.data.orders.append);
    const appendNagIdSource = useSelector((state: AppState) => state.data.orders.appendNagIdSource);
    const isFetching = useSelector((state: AppState) => state.data.orders.props.isFetching);
    const error = useSelector((state: AppState) => state.data.orders.props.isErrorGettingDetailedOrder);
    const userDeliveryAddresses = useSelector((state: AppState) => state.data.userInfo.userDeliveryAddresses); // addresses should be fetched in an earlier view
    const { isDesktop } = useDevice();
    const user = useUser();
    const isMounted = useIsMounted();

    const [deliveryAddress, setDeliveryAddress] = useState<UserDeliveryAddress | undefined>(props.detailedOrder?.address || undefined);
    const [contact, setContact] = useState(props.detailedOrder?.contact || '');
    const [phoneNumber, setPhoneNumber] = useState(props.detailedOrder?.phoneNumber || '');
    const [customerOrderNumber, setCustomerOrderNumber] = useState(props.detailedOrder?.customerOrderNumber || '');
    const [orderId, setOrderId] = useState(edit ? props.detailedOrder?.orderId || '' : '');
    const [orderNumber, setOrderNumber] = useState(edit ? props.detailedOrder?.orderNumber || '' : '');
    const [orderDesc, setOrderDesc] = useState(props.detailedOrder?.description || '');
    const [clientDesc, setClientDesc] = useState(props.detailedOrder?.customerDesc || '');
    const [rooferLogin, setRooferLogin] = useState(props.detailedOrder?.rooferLogin || '');

    const [rooferInfo, setRooferInfo] = useState(<span>Po podaniu poprawnego loginu wyświetlone zostaną dane w celu weryfikacji</span> as JSX.Element);
    const [phoneNumberInfo, setPhoneNumberInfo] = useState('');
    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
    const [addToExisting, setAddToExisting] = useState(edit ? props.detailedOrder?.addToExisting || false : false);
    const [rooferLoginCase, setRooferLoginCase] = useState(0);
    const [message, setMessage] = useState('defaultMessage');
    const [nextPage, setNextPage] = useState(false);
    const [show, setShow] = useState(false);
    const [orderBtnClicked, setOrderBtnClicked] = useState(false);
    const [addresses, setAddresses] = useState<UserDeliveryAddress[]>([]);
    const localCart = useSelector((state: AppState) => (state.data.orders.localCart));
    const cartTableContent = useSelector((state: AppState) => state.data.orders.cartTableContent);
    const isAddingOrder = useSelector((state: AppState) => state.data.orders.props.isAddingOrder);

    // prepare addresses to show in the select
    useEffect(() => {
        let address = userDeliveryAddresses ?? [];
        if (!_.isEmpty(props.detailedOrder) && props.detailedOrder.address !== undefined) { // if order fetched and not null
            if (!_.some(userDeliveryAddresses, (addr) => addr.id === props.detailedOrder.address?.id)) { // if address is not on the list
                const addr = { ...props.detailedOrder.address, readOnly: true }; // if its not on the list then it definetely cant be edited
                address = [addr, ...address]; // add address to the list
            }
        }
        setAddresses(address);
    }, [props.detailedOrder, userDeliveryAddresses]);

    useEffect(() => {
        setDeliveryAddress(props.detailedOrder?.address || undefined);
        setContact(props.detailedOrder?.contact || '');
        setPhoneNumber(props.detailedOrder?.phoneNumber || '');
        setCustomerOrderNumber(props.detailedOrder?.customerOrderNumber || '');
        setOrderId(edit ? props.detailedOrder?.orderId || '' : '');
        setOrderNumber(edit ? props.detailedOrder?.orderNumber || '' : '');
        setOrderDesc(props.detailedOrder?.description || '');
        setClientDesc(props.detailedOrder?.customerDesc || '');
        setRooferLogin(props.detailedOrder?.rooferLogin || '');
        setAddToExisting(edit ? props.detailedOrder?.addToExisting || false : false);
    }, [props.detailedOrder]);

    useEffect(() => {
        if (rooferLoginCase) {
            setRooferLoginCase(0);
            setRooferInfo(<span>Po podaniu poprawnego loginu wyświetlone zostaną dane w celu weryfikacji</span>);
        }
    }, [rooferLogin]);

    const changeCancelOrder = () => {
        props.setIsCancelModeOn(!props.isCancelModeOn);
    };

    if (isFetching || error) {
        return <Spinner showError={error} />;
    }

    const verifyRoofer = async (): Promise<boolean> => {
        // Returns true if login is roofer is verified or empty else false

        // If user left empty input then returns true
        if (!rooferLogin) {
            setRooferLoginCase(0);

            setRooferInfo(<span>Po podaniu poprawnego loginu wyświetlone zostaną dane w celu weryfikacji</span>);

            return true;
        }

        const info: RooferInfo = await getRoofer(rooferLogin);
        if (!isMounted.current) return false;
        // If rooferInfo has key error then returns false (it's invalid login case)
        if (info.error) {
            setRooferLoginCase(1);
            setRooferInfo(<span className='invalid-roofer-login-info'>Nie znaleziono dekarza pasującego do podanego loginu! Wpisz poprawny login dekarza lub pozostaw pole puste.</span>);
            return false;
        }

        // If there is serverError then returns false
        if (info.serverError) {
            setRooferLoginCase(1);
            setRooferInfo(<span className='invalid-roofer-login-info'>Napotkano błąd przy pobieraniu danych z serwera. Spróbuj ponownie za chwilę!</span>);
            return false;
        }

        // Else sets roofer info and returns true (it's valid login case)
        setRooferLoginCase(2);

        const phoneNo = info.phone.replace(/\d{6}(\d{3})/, '*** *** $1');
        setRooferInfo(
            <div className='roofer-info'>
                <p>
                    Dekarz:
                    <b>{info.login}</b>
                </p>
                <p>
                    Nazwa firmy:
                    <b>{info.company}</b>
                </p>
                <p>
                    NIP:
                    <b>{info.nip}</b>
                </p>
                {' '}
                <p>
                    Telefon:
                    <b>{phoneNo}</b>
                </p>
            </div>
        );

        return true;
    };

    const verifyPhoneNumber = async (): Promise<boolean> => {
        if (phoneNumber === '') {
            if (!deliveryAddress || deliveryAddress.default) {
                setPhoneNumberInfo('');
                setIsPhoneNumberValid(true);
                return true;
            }
            setPhoneNumberInfo('Pole obowiązkowe dla adresu dostawy innego niż domyślny.');
            setIsPhoneNumberValid(false);
            return false;
        }
        if (!isValidPhoneNumber(phoneNumber, { defaultCountry: 'PL' }) || parsePhoneNumberWithError(phoneNumber, { defaultCountry: 'PL', extract: false }) === undefined) {
            setPhoneNumberInfo('Nieprawidłowy numer telefonu');
            setIsPhoneNumberValid(false);
            return false;
        }
        setPhoneNumberInfo('');
        setIsPhoneNumberValid(true);
        return true;
    };

    const validateOrder = async () => {
        let isValid = true;

        setRooferLoginCase(0);
        setRooferInfo(<span>Po podaniu poprawnego loginu wyświetlone zostaną dane w celu weryfikacji</span>);

        if (!(await verifyRoofer()) || !(await verifyPhoneNumber())) {
            isValid = false;
        }

        return isValid;
    };

    const sendOrder = async () => {
        const products: ProductInOrder[] = [];

        _.map(localCart.productsInLocalCart, (p) => {
            products.push({
                product: p.product,
                quantity: p.quantity * (p.unit?.converter ?? 1),
                dimension: p.dimension,
                additionsSelected: p.additionsSelected,
                unit: cartMethods.getDefaultUnitOfProduct(p.product).unit
            });
        });

        const added = edit ?
            await modifyOrder({
                orderId,
                address: deliveryAddress,
                contact,
                phoneNumber,
                customerDesc: clientDesc,
                description: orderDesc,
                addToExisting,
                rooferLogin,
                orderNumber,
                products,
                customerOrderNumber
            }) : (
                append ? (
                    await appendOrder({
                        orderId,
                        address: deliveryAddress,
                        contact,
                        phoneNumber,
                        customerDesc: clientDesc,
                        description: orderDesc,
                        addToExisting,
                        rooferLogin,
                        products,
                        customerOrderNumber
                    }, appendNagIdSource ?? '', cartTableContent.summaryPrice)
                ) : (
                    await addOrder({
                        orderId,
                        address: deliveryAddress,
                        contact,
                        phoneNumber,
                        customerDesc: clientDesc,
                        description: orderDesc,
                        addToExisting,
                        rooferLogin,
                        products,
                        customerOrderNumber
                    }, cartTableContent.summaryPrice)
                ));

        if (added) {
            if (edit) setMessage('Zamówienie zostało pomyślnie zmodyfikowane');
            else if (append) setMessage('Domówienie do zamówienia zostało pomyślnie złożone');
            else setMessage('Zamówienie zostało pomyślnie złożone');
            store.dispatch(dataActions.setDetailedOrder({} as DetailedOrder));
            setNextPage(true);
            setShow(true);
        }
    };

    const handleSubmit = async (event: React.MouseEvent) => {
        if (!orderBtnClicked) {
            setOrderBtnClicked(true);
            event.stopPropagation();
            if (await validateOrder()) {
                sendOrder();
            } else {
                setOrderBtnClicked(false);
            }
        }
    };

    return (
        <div className='NewOrder-summarySection'>
            {isAddingOrder && <div className='NewOrder-summary-addingOrder-overlay'></div>}
            <div className='NewOrder-summaryLeftBar'>
                <Modal show={props.isCancelModeOn} onHide={changeCancelOrder} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Uwaga</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{ textAlign: 'center' }}>
                            {edit ? 'Czy na pewno chcesz anulować edycję zamówienia?' : (
                                <>
                                    <p>Czy na pewno chcesz anulować zamówienie?</p>
                                    <p>Spowoduje to wyczyszczenie koszyka</p>
                                </>
                            )}
                        </div>
                        <div className='NewOrder-summaryCancelConfirm-buttons'>
                            <ButtonComponent
                                marginLeft='0'
                                marginRight='0'
                                width='45%'
                                text='TAK'
                                onClick={() => {
                                    store.dispatch(dataActions.setDetailedOrder({} as DetailedOrder));
                                    if (edit) {
                                        history.push(`${process.env.PUBLIC_URL}/orders`);
                                        store.dispatch(dataActions.clearLocalCart());
                                        store.dispatch(dataActions.setEdit(false));
                                    } else {
                                        if (append) {
                                            store.dispatch(dataActions.setAppend(false));
                                        }
                                        props.goToAssortmentSelection();
                                        store.dispatch(dataActions.clearLocalCart());
                                    }
                                    changeCancelOrder();
                                }}
                                onHoverAnimation={3}
                            />
                            <ButtonComponent
                                marginLeft='10%'
                                marginRight='0'
                                width='45%'
                                text='NIE'
                                onClick={changeCancelOrder}
                                onHoverAnimation={3}
                            />
                        </div>
                    </Modal.Body>
                </Modal>
                <Card className='NewOrder-summaryCard'>
                    <Card.Header>
                        Podsumowanie zamówienia
                    </Card.Header>
                    <Card.Body>
                        <ListGroup className='list-group-flush'>
                            <ListGroupItem className='NewOrder-summary NewOrder-summaryOrder'>
                                <Card.Title className='NewOrder-summaryClientData'>PODSUMOWANIE</Card.Title>
                                <Card.Subtitle className='NewOrder-summaryClientSubtitle'>
                                    <span>Twoje produkty w koszyku:</span>
                                    <span>{cartTableContent.itemsCount}</span>
                                </Card.Subtitle>
                                <hr style={{ marginTop: '5px' }} />
                                <div className='NewOrder-summaryLabel'>Waluta:</div>
                                <div className='NewOrder-summaryValue'>{user?.currency}</div>
                                <div className='NewOrder-summaryLabel'>Wartość netto przed rabatem:</div>
                                <div className='NewOrder-summaryValue'>{cartTableContent.summaryPriceBeforeDiscount?.toFixed(2)}</div>
                                <div className='NewOrder-summaryLabel'>Rabat:</div>
                                <div className='NewOrder-summaryValue'>
                                    -{cartTableContent.summaryDiscount?.toFixed(2)}
                                </div>
                                <div className='NewOrder-summaryLabel'>Wartość netto po rabacie:</div>
                                <div className='NewOrder-summaryValue'>{(cartTableContent.summaryPriceBeforeDiscount - cartTableContent.summaryDiscount)?.toFixed(2)}</div>
                                <div className='NewOrder-summaryLabel'>VAT:</div>
                                <div className='NewOrder-summaryValue'>{cartTableContent.summaryVat?.toFixed(2)}</div>
                            </ListGroupItem>
                            <ListGroupItem className='NewOrder-summary'>
                                <div className='NewOrder-summaryLabel'><b>Brutto:</b></div>
                                <div className='NewOrder-summaryValue'>
                                    <b>{(cartTableContent.summaryPrice)?.toFixed(2)}</b>
                                </div>
                            </ListGroupItem>
                        </ListGroup>
                    </Card.Body>
                </Card>
                {isDesktop && <ButtonComponent
                    marginLeft='0'
                    marginRight='0'
                    marginTop='5px'
                    marginBottom='0'
                    width='100%'
                    text={step === OrderStep.OrderSummary ? (edit ? 'DODAJ PRODUKTY' : 'POWRÓT DO WYBORU ASORTYMENTU') : 'POWRÓT DO PODSUMOWANIA'}
                    onClick={step === OrderStep.OrderSummary ? () => props.goToAssortmentSelection() : () => props.goToSummary()}
                />}
                {isDesktop && (
                    <div className='NewOrder-summary-buttons'>
                        <ButtonComponent
                            text={edit ? 'ANULUJ EDYCJĘ ' : 'ANULUJ ZAMÓWIENIE'}
                            marginLeft='0'
                            marginRight='0'
                            marginTop='5px'
                            variant='danger'
                            onClick={changeCancelOrder}
                            visibility={step === OrderStep.SummaryForm ? 'hidden' : 'visible'}
                            brandColoredButton={false}
                        />
                        <ButtonComponent
                            text={isAddingOrder ? undefined : (edit ? 'ZATWIERDŹ EDYCJĘ' : (append ? 'WYSTAW DOMÓWIENIE' : 'ZŁÓŻ ZAMÓWIENIE'))}
                            marginLeft='0'
                            marginRight='0'
                            marginTop='5px'
                            variant='success'
                            onClick={step === OrderStep.SummaryForm ? handleSubmit : props.goToOrderForm}
                            disabled={_.isEmpty(localCart.productsInLocalCart)}
                            brandColoredButton={false}
                        >
                            {isAddingOrder ? <ReactSpinner animation='border' style={{ verticalAlign: 'middle', margin: 0 }} /> : undefined}
                        </ButtonComponent>
                    </div>
                )}
            </div>
            <div className='NewOrder-summaryProducts'>
                {step === OrderStep.OrderSummary ? <CartTable /> : <NewOrderForm
                    completeAction={props.completeAction}
                    {...{
                        deliveryAddress,
                        setDeliveryAddress,
                        contact,
                        setContact,
                        phoneNumber,
                        setPhoneNumber,
                        addresses,
                        customerOrderNumber,
                        setCustomerOrderNumber,
                        orderNumber,
                        orderDesc,
                        setOrderDesc,
                        clientDesc,
                        setClientDesc,
                        rooferLogin,
                        setRooferLogin,
                        rooferInfo,
                        rooferLoginCase,
                        addToExisting,
                        setAddToExisting,
                        verifyRoofer,
                        handleSubmit,
                        message,
                        nextPage,
                        show,
                        setShow,
                        phoneNumberInfo,
                        verifyPhoneNumber,
                        isPhoneNumberValid
                    }}
                />}
            </div>
            {!isDesktop && (
                <div className='NewOrder-footer NewOrder-summary-footer'>
                    <ButtonComponent
                        marginTop='2px'
                        marginBottom='2px'
                        marginLeft='0'
                        marginRight='0'
                        height='54px'
                        width='32%'
                        text={step === OrderStep.OrderSummary ? (edit ? 'DODAJ PRODUKTY' : 'KONTYNUUJ ZAMÓWIENIE') : 'POWRÓT'}
                        onClick={step === OrderStep.OrderSummary ? () => props.goToAssortmentSelection() : () => props.goToSummary()}
                    />
                    <ButtonComponent
                        marginTop='2px'
                        marginBottom='2px'
                        marginLeft='0'
                        marginRight='0'
                        height='54px'
                        width='32%'
                        variant='danger'
                        text={edit ? 'ANULUJ EDYCJĘ ' : 'ANULUJ ZAMÓWIENIE'}
                        onClick={changeCancelOrder}
                        visibility={step === OrderStep.SummaryForm ? 'hidden' : 'visible'}
                        brandColoredButton={false}
                    />
                    <ButtonComponent
                        marginTop='2px'
                        marginBottom='2px'
                        marginLeft='0'
                        marginRight='0'
                        height='54px'
                        width='32%'
                        text={isAddingOrder ? undefined : (edit ? 'ZATWIERDŹ EDYCJĘ' : (append ? 'WYSTAW DOMÓWIENIE' : 'ZŁÓŻ ZAMÓWIENIE'))}
                        onClick={step === OrderStep.SummaryForm ? handleSubmit : props.goToOrderForm}
                        disabled={_.isEmpty(localCart.productsInLocalCart)}
                        backgroundColor='#070'
                        brandColoredButton={false}
                    >
                        {isAddingOrder ? (
                            <ReactSpinner animation='border' style={{ verticalAlign: 'middle' }} />
                        ) : undefined}
                    </ButtonComponent>
                </div>
            )}
        </div>
    );
};

export default NewOrderSummary;
